/**
 * Utilidades para parsear y serializar pasajeros desde/hacia el campo `observaciones`
 * de un servicio.
 *
 * Formato esperado (cuando hay múltiples pasajeros) en observaciones:
 *
 *   {observaciones del usuario}
 *
 *   --- Detalle de Ruta (orden de recogida) ---
 *   1. Juan Pérez: ORIGEN1 → DESTINO1
 *   2. María López: ORIGEN2 → DESTINO2
 *
 *   --- Contactos ---
 *   1. Juan Pérez | Tel: +56... | Email: juan@...
 *   2. María López | Tel: +56... | Email: maria@...
 */

const SEPARADOR_RUTA = '--- Detalle de Ruta (orden de recogida) ---';
const SEPARADOR_CONTACTOS = '--- Contactos ---';

const limpiar = (s) => (s ?? '').toString().trim();

export function pasajeroVacio() {
  return { nombre: '', telefono: '', correo: '', origen: '', destino: '' };
}

/**
 * Parsea el servicio para extraer la lista de pasajeros y las observaciones del usuario.
 * Funciona para servicios con 1 pasajero (datos en columnas) o con N (datos en observaciones).
 */
export function parsearPasajerosDeServicio(servicio) {
  if (!servicio) {
    return { pasajeros: [pasajeroVacio()], observacionesUsuario: '' };
  }

  const obs = servicio.observaciones || '';

  // Si no contiene el bloque estructurado, es un servicio de 1 pasajero
  if (!obs.includes(SEPARADOR_RUTA)) {
    return {
      pasajeros: [{
        nombre: servicio.nombre_pasajero || '',
        telefono: servicio.telefono || '',
        correo: servicio.correo || '',
        origen: servicio.origen || '',
        destino: servicio.destino || ''
      }],
      observacionesUsuario: obs
    };
  }

  // Separar las 3 partes
  const idxRuta = obs.indexOf(SEPARADOR_RUTA);
  const idxContactos = obs.indexOf(SEPARADOR_CONTACTOS);

  const observacionesUsuario = limpiar(obs.substring(0, idxRuta));

  const bloqueRuta = idxContactos > -1
    ? obs.substring(idxRuta + SEPARADOR_RUTA.length, idxContactos)
    : obs.substring(idxRuta + SEPARADOR_RUTA.length);

  const bloqueContactos = idxContactos > -1
    ? obs.substring(idxContactos + SEPARADOR_CONTACTOS.length)
    : '';

  // Parse del bloque de Ruta: "1. Nombre: ORIGEN → DESTINO" (separador puede ser → o ->)
  const lineasRuta = bloqueRuta.split('\n').map(l => l.trim()).filter(Boolean);
  const pasajeros = [];

  lineasRuta.forEach((linea) => {
    // Quitar prefijo "N. "
    const sinIndice = linea.replace(/^\d+\.\s*/, '');
    // Separar nombre y ruta por ":"
    const dosPuntos = sinIndice.indexOf(':');
    if (dosPuntos === -1) return;
    const nombre = sinIndice.substring(0, dosPuntos).trim();
    const ruta = sinIndice.substring(dosPuntos + 1);
    const partesRuta = ruta.split(/→|->/);
    const origen = limpiar(partesRuta[0]);
    const destino = limpiar(partesRuta[1] || '');
    pasajeros.push({ nombre, telefono: '', correo: '', origen, destino });
  });

  // Parse del bloque de Contactos: "1. Nombre | Tel: ... | Email: ..."
  const lineasContactos = bloqueContactos.split('\n').map(l => l.trim()).filter(Boolean);
  lineasContactos.forEach((linea) => {
    const sinIndice = linea.replace(/^\d+\.\s*/, '');
    const partes = sinIndice.split('|').map(p => p.trim());
    if (partes.length === 0) return;
    const nombre = partes[0];
    let telefono = '';
    let correo = '';
    partes.slice(1).forEach(parte => {
      const lower = parte.toLowerCase();
      if (lower.startsWith('tel')) {
        telefono = parte.substring(parte.indexOf(':') + 1).trim();
      } else if (lower.startsWith('email') || lower.startsWith('mail') || lower.startsWith('correo')) {
        correo = parte.substring(parte.indexOf(':') + 1).trim();
      }
    });
    // Buscar pasajero existente por nombre (mismo orden)
    const idx = pasajeros.findIndex(p => p.nombre.toLowerCase() === nombre.toLowerCase());
    if (idx >= 0) {
      pasajeros[idx].telefono = telefono;
      pasajeros[idx].correo = correo;
    } else {
      pasajeros.push({ nombre, telefono, correo, origen: '', destino: '' });
    }
  });

  // Fallback si quedó vacío por algún motivo
  if (pasajeros.length === 0) {
    pasajeros.push({
      nombre: servicio.nombre_pasajero || '',
      telefono: servicio.telefono || '',
      correo: servicio.correo || '',
      origen: servicio.origen || '',
      destino: servicio.destino || ''
    });
  }

  return { pasajeros, observacionesUsuario };
}

/**
 * Devuelve el origen y destino "visibles" del servicio.
 * Si tiene múltiples pasajeros con orígenes/destinos distintos, los devuelve
 * numerados y separados por " / " (útil para labels compactos).
 * Si todos coinciden, devuelve el valor único (no repite).
 * Para 1 pasajero, devuelve los campos raw del servicio.
 */
export function resumirRutaServicio(servicio) {
  const { pasajeros } = parsearPasajerosDeServicio(servicio);
  if (!pasajeros || pasajeros.length <= 1) {
    return {
      origen: servicio?.origen || '',
      destino: servicio?.destino || ''
    };
  }
  const dedupe = (arr) => {
    const seen = new Set();
    const out = [];
    arr.forEach(v => { if (v && !seen.has(v)) { seen.add(v); out.push(v); } });
    return out;
  };
  const origenes = dedupe(pasajeros.map(p => p.origen));
  const destinos = dedupe(pasajeros.map(p => p.destino));
  const origen = origenes.length <= 1
    ? (origenes[0] || servicio?.origen || '')
    : origenes.map((o, i) => `${i + 1}. ${o}`).join(' / ');
  const destino = destinos.length <= 1
    ? (destinos[0] || servicio?.destino || '')
    : destinos.map((d, i) => `${i + 1}. ${d}`).join(' / ');
  return { origen, destino };
}

/**
 * Convierte la lista de pasajeros + observaciones del usuario al formato que entiende
 * el backend (los campos del servicio + observaciones serializadas).
 */
export function serializarPasajeros(pasajeros, observacionesUsuario = '') {
  if (!pasajeros || pasajeros.length === 0) {
    return {
      pasajeros: '',
      nombre_pasajero: '',
      telefono: '',
      correo: '',
      origen: '',
      destino: '',
      observaciones: observacionesUsuario || ''
    };
  }

  const primero = pasajeros[0];
  const pasajerosStr = pasajeros.map(p => p.nombre).join(', ');

  if (pasajeros.length === 1) {
    return {
      pasajeros: pasajerosStr,
      nombre_pasajero: primero.nombre,
      telefono: primero.telefono || '',
      correo: primero.correo || '',
      origen: primero.origen,
      destino: primero.destino,
      observaciones: observacionesUsuario || ''
    };
  }

  // Múltiples pasajeros -> serializar a observaciones
  const rutaDetalle = pasajeros.map((p, i) =>
    `${i + 1}. ${p.nombre}: ${p.origen} → ${p.destino}`
  ).join('\n');

  const contactos = pasajeros.map((p, i) => {
    const partes = [`${i + 1}. ${p.nombre}`];
    if (p.telefono) partes.push(`Tel: ${p.telefono}`);
    if (p.correo) partes.push(`Email: ${p.correo}`);
    return partes.join(' | ');
  }).join('\n');

  const observaciones = `${observacionesUsuario || ''}\n\n${SEPARADOR_RUTA}\n${rutaDetalle}\n\n${SEPARADOR_CONTACTOS}\n${contactos}`.trim();

  const ultimo = pasajeros[pasajeros.length - 1];
  return {
    pasajeros: pasajerosStr,
    nombre_pasajero: primero.nombre,
    telefono: primero.telefono || '',
    correo: primero.correo || '',
    origen: primero.origen,
    destino: ultimo.destino || '',
    observaciones
  };
}
